import React, { useEffect, useMemo, useState } from "react";
import { DumbbellAxis, DumbbellLegend, DumbbellTrack, PURPLE, TEAL } from "@/components/Dumbbell";
import { pickPortfolio, rememberPortfolio } from "./selectedPortfolio";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "@/components/ui/sonner";
import {
  Loader2, GitCompare, Trophy, AlertTriangle, Save, Download, Lock,
  ArrowLeft, ChevronRight, Sparkles, Trash2, FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API, fmtScore } from "./shared";
import { OverallRiskBanner, riskBadgeClass } from "./ExecutiveClarity";

const AVAIL = {
  available: { label: "Available", cls: "text-[#065f46] bg-[#ecfdf5] border-[#a7f3d0]" },
  no_diagnostic: { label: "No completed Diagnostic", cls: "text-[#6b7280] bg-[#f3f4f6] border-[#e5e7eb]" },
  different_version: { label: "Different Diagnostic Version", cls: "text-[#a16207] bg-[#fefce8] border-[#fde047]" },
  no_score: { label: "Incomplete Diagnostic", cls: "text-[#a16207] bg-[#fefce8] border-[#fde047]" },
};
const PRIO = { High: "text-[#b91c1c] bg-[#fef2f2] border-[#fecaca]", Medium: "text-[#a16207] bg-[#fefce8] border-[#fde047]", Low: "text-[#065f46] bg-[#ecfdf5] border-[#a7f3d0]" };
const gapColor = (band) => ({ small: "#10b981", moderate: "#facc15", significant: "#007a9e", large: "#ef4444" }[band] || "#9ca3af");

const Section = ({ title, sub, children, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-[#e5e7eb] bg-white p-5">
    <h3 className="text-base font-bold text-[#111827]">{title}</h3>
    {sub && <p className="text-[12px] text-[#6b7280] mt-0.5">{sub}</p>}
    <div className="mt-4">{children}</div>
  </div>
);

export default function BenchmarkAnalysis() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portfolioId, setPortfolioId] = useState(params.get("pid") || null);
  const [portfolios, setPortfolios] = useState([]);

  const [selected, setSelected] = useState([]);
  const [benchmarkTid, setBenchmarkTid] = useState(null);
  const [scope, setScope] = useState("management_controllable");
  const [analysis, setAnalysis] = useState(null);
  const [running, setRunning] = useState(false);
  const [view, setView] = useState("select"); // select | results | saved
  const [saved, setSaved] = useState([]);

  // Resolve portfolio (use ?pid or first owned)
  useEffect(() => {
    (async () => {
      const r = await fetch(`${API}/api/portfolio/me`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setPortfolios(d.portfolios || []);
        if (!portfolioId && (d.portfolios || []).length) setPortfolioId(pickPortfolio(d.portfolios).id);
      }
    })();
  }, []); // eslint-disable-line

  const loadConfig = async (pid) => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/portfolio/${pid}/benchmark/config`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setConfig(d);
        setScope(d.default_scope || "management_controllable");
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { if (portfolioId) loadConfig(portfolioId); }, [portfolioId]); // eslint-disable-line

  const availableSchools = useMemo(() => (config?.schools || []).filter((s) => s.availability === "available"), [config]);

  // auto-benchmark = highest selected
  useEffect(() => {
    if (!selected.length) { setBenchmarkTid(null); return; }
    const chosen = (config?.schools || []).filter((s) => selected.includes(s.tenant_id));
    const top = chosen.reduce((a, b) => ((b.overall_score ?? -1) > (a.overall_score ?? -1) ? b : a), chosen[0]);
    if (!benchmarkTid || !selected.includes(benchmarkTid)) setBenchmarkTid(top?.tenant_id || null);
  }, [selected]); // eslint-disable-line

  const toggle = (tid) => {
    setSelected((prev) => {
      if (prev.includes(tid)) return prev.filter((x) => x !== tid);
      if (prev.length >= (config?.max_schools || 3)) {
        toast.error(`You can compare a maximum of ${config?.max_schools || 3} schools at a time.`);
        return prev;
      }
      return [...prev, tid];
    });
  };

  const run = async (save = false) => {
    if (selected.length < 2) { toast.error("Select at least two schools."); return; }
    setRunning(true);
    try {
      const url = `${API}/api/portfolio/${portfolioId}/benchmark/${save ? "save" : "run"}`;
      const r = await fetch(url, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenant_ids: selected, benchmark_tenant_id: benchmarkTid, scope }),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || "Analysis failed"); return; }
      if (save) { toast.success("Analysis saved"); return; }
      setAnalysis(d); setView("results");
    } finally { setRunning(false); }
  };

  const loadSaved = async () => {
    const r = await fetch(`${API}/api/portfolio/${portfolioId}/benchmark/saved`, { credentials: "include" });
    if (r.ok) setSaved((await r.json()).analyses || []);
    setView("saved");
  };
  const openSaved = async (id) => {
    const r = await fetch(`${API}/api/portfolio/${portfolioId}/benchmark/saved/${id}`, { credentials: "include" });
    if (r.ok) { const d = await r.json(); setAnalysis(d.payload); setView("results"); }
  };
  const deleteSaved = async (id) => {
    if (!window.confirm("Delete this saved analysis?")) return;
    const r = await fetch(`${API}/api/portfolio/${portfolioId}/benchmark/saved/${id}`, { method: "DELETE", credentials: "include" });
    if (r.ok) { toast.success("Deleted"); loadSaved(); }
  };

  const exportCsv = () => {
    if (!analysis) return;
    const rows = [["School", "Overall Score", "Gap to Benchmark", "Risk", "Completion %", "Evidence %"]];
    analysis.score_gap.forEach((s) => rows.push([s.school_name, s.overall_score, s.gap_to_benchmark, s.risk_level, s.completion_pct, s.evidence_completion_pct]));
    rows.push([]); rows.push(["Recommended Actions"]);
    rows.push(["School", "Pillar", "Action", "Owner", "Timeline", "Priority", "Expected Impact"]);
    Object.entries(analysis.recommendations).forEach(([tid, recos]) => {
      const nm = analysis.score_gap.find((x) => x.tenant_id === tid)?.school_name || tid;
      recos.forEach((r) => rows.push([nm, r.pillar_name, r.action_title, r.suggested_owner_role, r.suggested_timeline, r.priority, r.expected_score_impact]));
    });
    const csv = rows.map((r) => r.map((c) => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `benchmark-analysis-${Date.now()}.csv`; a.click();
  };

  if (loading || !config) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#9ca3af]" /></div>;

  if (!config.enabled) {
    return (
      <div data-testid="benchmark-locked" className="mx-auto mt-16 max-w-md rounded-2xl border border-[#e5e7eb] bg-white p-8 text-center">
        <Lock className="mx-auto h-10 w-10 text-[#9ca3af]" />
        <h2 className="mt-4 text-lg font-bold text-[#111827]">Benchmark Improvement Analysis</h2>
        <p className="mt-2 text-sm text-[#6b7280]">This feature is not enabled for your portfolio. Please contact your 589 Labs administrator.</p>
      </div>
    );
  }

  const nameOf = (tid) => (config.schools.find((s) => s.tenant_id === tid)?.school_name) || tid;

  return (
    <div data-testid="benchmark-page" className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-[#111827]"><GitCompare className="h-5 w-5 text-[#b91c1c]" /><h1 className="text-2xl font-bold">Benchmark Improvement Analysis</h1></div>
          <p className="mt-1 text-sm text-[#6b7280]">Compare up to {config.max_schools} schools, set a benchmark, and identify management-focused improvement actions.</p>
        </div>
        <div className="flex items-center gap-2">
          {view !== "select" && <Button variant="outline" size="sm" onClick={() => setView("select")} data-testid="benchmark-back-select"><ArrowLeft className="h-4 w-4 mr-1" /> New Analysis</Button>}
          <Button variant="outline" size="sm" onClick={loadSaved} data-testid="benchmark-view-saved"><FileText className="h-4 w-4 mr-1" /> Saved Analyses</Button>
        </div>
      </div>

      {portfolios.length > 1 && (
        <Select value={portfolioId} onValueChange={(v) => { setPortfolioId(v); rememberPortfolio(v); setSelected([]); setAnalysis(null); setView("select"); }}>
          <SelectTrigger className="w-72" data-testid="benchmark-portfolio-switcher"><SelectValue /></SelectTrigger>
          <SelectContent>{portfolios.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
      )}

      {/* ── SELECT ── */}
      {view === "select" && (
        <>
          {availableSchools.length < 2 ? (
            <div className="rounded-xl border border-[#e5e7eb] bg-white p-8 text-center text-sm text-[#6b7280]">
              Benchmark Analysis requires at least two schools with completed Diagnostic data.
            </div>
          ) : (
            <Section title="Select schools to compare" sub={`Choose 2 to ${config.max_schools} schools. The highest-scoring selection becomes the benchmark (you can change it).`} testid="benchmark-selector">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {config.schools.map((s) => {
                  const av = AVAIL[s.availability] || AVAIL.no_diagnostic;
                  const disabled = s.availability !== "available";
                  const checked = selected.includes(s.tenant_id);
                  return (
                    <label key={s.tenant_id} data-testid={`benchmark-school-${s.tenant_id}`}
                      className={`rounded-lg border p-3 flex gap-3 ${disabled ? "opacity-60 bg-[#f9fafb]" : "cursor-pointer hover:border-[#b91c1c]"} ${checked ? "border-[#b91c1c] ring-1 ring-[#fecaca]" : "border-[#e5e7eb]"}`}>
                      <Checkbox checked={checked} disabled={disabled} onCheckedChange={() => toggle(s.tenant_id)} className="mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <div className="text-[14px] font-bold text-[#111827] truncate">{s.school_name}</div>
                        <div className="text-[11px] text-[#6b7280]">{s.latest_period || "No period"} · {s.overall_score != null ? `${s.overall_score}/100` : "—"} · {s.completion_pct}% complete</div>
                        <span className={`inline-block mt-1.5 text-[10px] font-semibold px-2 py-0.5 rounded border ${av.cls}`}>{av.label}</span>
                      </div>
                    </label>
                  );
                })}
              </div>

              {selected.length >= 1 && (
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-[#6b7280] font-semibold">Benchmark (Reference) School</label>
                    <Select value={benchmarkTid || ""} onValueChange={setBenchmarkTid}>
                      <SelectTrigger className="mt-1" data-testid="benchmark-select"><SelectValue placeholder="Auto: highest score" /></SelectTrigger>
                      <SelectContent>{selected.map((t) => <SelectItem key={t} value={t}>{nameOf(t)}</SelectItem>)}</SelectContent>
                    </Select>
                    <p className="text-[10px] text-[#9ca3af] mt-1">Defaults to the highest current Diagnostic score.</p>
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-[#6b7280] font-semibold">Analysis Scope</label>
                    <Select value={scope} onValueChange={setScope}>
                      <SelectTrigger className="mt-1" data-testid="benchmark-scope"><SelectValue /></SelectTrigger>
                      <SelectContent>{config.scopes.map((sc) => <SelectItem key={sc.id} value={sc.id}>{sc.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <p className="text-[10px] text-[#9ca3af] mt-1">Defaults to management-controllable areas.</p>
                  </div>
                </div>
              )}

              <div className="mt-5 flex items-center justify-between">
                <span className="text-[12px] text-[#6b7280]">{selected.length} selected (min 2, max {config.max_schools})</span>
                <Button onClick={() => run(false)} disabled={selected.length < 2 || running} data-testid="benchmark-run-btn" className="bg-[#b91c1c] hover:bg-[#991b1b] text-white">
                  {running ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <GitCompare className="h-4 w-4 mr-1" />} Run Comparison
                </Button>
              </div>
            </Section>
          )}
        </>
      )}

      {/* ── SAVED ── */}
      {view === "saved" && (
        <Section title="Saved Analyses" testid="benchmark-saved-list">
          {!saved.length ? <div className="text-sm text-[#6b7280] py-6 text-center">No saved analyses yet.</div> : (
            <div className="divide-y divide-[#e5e7eb]">
              {saved.map((a) => (
                <div key={a.id} data-testid={`benchmark-saved-${a.id}`} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[14px] font-bold text-[#111827]">{a.selected_tenant_ids.length} schools · {a.analysis_scope.replace(/_/g, " ")}</div>
                    <div className="text-[11px] text-[#6b7280]">{new Date(a.created_at).toLocaleString()} · by {a.generated_by_name || "—"}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => openSaved(a.id)} data-testid={`benchmark-open-${a.id}`}>Open <ChevronRight className="h-3.5 w-3.5 ml-1" /></Button>
                    <button onClick={() => deleteSaved(a.id)} data-testid={`benchmark-delete-${a.id}`} className="p-1.5 rounded text-[#9ca3af] hover:bg-[#fef2f2] hover:text-[#b91c1c]"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      {/* ── RESULTS ── */}
      {view === "results" && analysis && <BenchmarkResults analysis={analysis} config={config} portfolioId={portfolioId} onSave={() => run(true)} onExport={exportCsv} running={running} />}
    </div>
  );
}

const MARKS = [
  { shape: "diamond", color: PURPLE }, { shape: "square", color: "#b45309" }, { shape: "triangle", color: "#0f766e" }, { shape: "circle", color: "#1f2937", hollow: true },
];

// One row per pillar: benchmark (teal circle) is the reference; each school gets its own marker and gap line.
const BenchmarkDumbbell = ({ a, nonBench, mode, setMode }) => {
  const series = [{ key: a.benchmark.tenant_id, label: `${a.benchmark.school_name || "Benchmark"} (benchmark)`, shape: "circle", color: TEAL },
    ...nonBench.map((sc, i) => ({ key: sc.tenant_id, label: sc.school_name, ...MARKS[i % MARKS.length] }))];
  const seg = (on) => `min-h-[40px] px-3 text-[13px] font-semibold ${on ? "bg-[#111827] text-white" : "bg-white text-[#111827] hover:bg-[#f3f4f6]"}`;
  return (
    <div className="mb-3" data-testid="benchmark-dumbbell">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <DumbbellLegend series={series} />
        <div className="inline-flex overflow-hidden rounded-md border border-[#d1d5db]" role="group" aria-label="View">
          <button type="button" aria-pressed={mode === "chart"} className={seg(mode === "chart")} onClick={() => setMode("chart")} data-testid="benchmark-mode-chart">Chart</button>
          <button type="button" aria-pressed={mode === "table"} className={seg(mode === "table")} onClick={() => setMode("table")} data-testid="benchmark-mode-table">Table</button>
        </div>
      </div>
      {mode === "chart" && (
        <ul className="mt-3 divide-y divide-[#f3f4f6] border-t border-[#f3f4f6]">
          <li className="hidden grid-cols-[200px_1fr] gap-3 px-1 pt-2 min-[701px]:grid"><span /><DumbbellAxis /></li>
          {a.pillar_gap.map((p) => {
            const values = { [a.benchmark.tenant_id]: p.benchmark_score };
            nonBench.forEach((sc) => { values[sc.tenant_id] = (p.schools.find((x) => x.tenant_id === sc.tenant_id) || {}).score ?? null; });
            const sr = series.map((x) => `${x.label} ${values[x.key] ?? "no score"}`).join(", ");
            return (
              <li key={p.pillar_id} className={`grid grid-cols-1 gap-1 px-1 py-2 min-[701px]:grid-cols-[200px_1fr] min-[701px]:items-center min-[701px]:gap-3 ${p.in_scope ? "" : "opacity-60"}`} data-testid={`benchmark-dumbbell-row-${p.pillar_id}`}>
                <span className="sr-only">{p.pillar_name}: {sr}</span>
                <span className="text-[13.5px] font-semibold text-[#111827]" aria-hidden>{p.pillar_name}</span>
                <DumbbellTrack values={values} series={series} refKey={a.benchmark.tenant_id} />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

function BenchmarkResults({ analysis, config, portfolioId, onSave, onExport, running }) {
  const a = analysis;
  const nonBench = a.score_gap.filter((s) => !s.is_benchmark);
  const [oppSort, setOppSort] = useState("gap");
  const [pgMode, setPgMode] = useState("chart");
  const [aiData, setAiData] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const resultsRef = React.useRef(null);

  const aiBody = useMemo(() => ({
    tenant_ids: a.score_gap.map((s) => s.tenant_id),
    benchmark_tenant_id: a.benchmark.tenant_id,
    scope: a.scope,
  }), [a]);

  const opps = useMemo(() => {
    const arr = [...a.indicator_opportunities];
    if (oppSort === "gap") arr.sort((x, y) => y.gap - x.gap);
    if (oppSort === "priority") { const r = { High: 0, Medium: 1, Low: 2 }; arr.sort((x, y) => r[x.priority] - r[y.priority]); }
    if (oppSort === "evidence") arr.sort((x, y) => (x.evidence_status === "Missing" ? -1 : 1));
    return arr;
  }, [a, oppSort]);

  const generateAI = async () => {
    setAiLoading(true);
    try {
      const r = await fetch(`${API}/api/portfolio/${portfolioId}/benchmark/ai-recommendations`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(aiBody),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || "AI generation failed"); return; }
      if (d.ai?.error) { toast.error(d.ai.error); return; }
      setAiData(d.ai);
      toast.success("AI recommendations generated");
    } finally { setAiLoading(false); }
  };

  const exportPdf = async () => {
    if (!resultsRef.current) return;
    setPdfLoading(true);
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      await html2pdf().set({
        margin: 10, filename: `benchmark-analysis-${Date.now()}.pdf`,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"] },
      }).from(resultsRef.current).save();
    } catch (e) {
      toast.error("PDF export failed");
    } finally { setPdfLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="benchmark-results" ref={resultsRef}>
      {/* 1. Benchmark Summary */}
      <Section title="Benchmark Summary" testid="benchmark-summary">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-[#ecfdf5] border border-[#a7f3d0] flex items-center justify-center"><Trophy className="h-6 w-6 text-[#065f46]" /></div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#6b7280] font-semibold">Benchmark (Reference) School</div>
              <div className="text-lg font-bold text-[#111827]" data-testid="benchmark-school-name">{a.benchmark.school_name}</div>
              <div className="text-[12px] text-[#6b7280]">{a.benchmark.reason} · {a.benchmark.score}/100 · Scope: {a.scope_label}</div>
            </div>
          </div>
        </div>
        <div className="mt-4 rounded-lg bg-[#fffbeb] border border-[#fde68a] p-3 text-[12px] text-[#92400e]">
          This analysis compares controllable school management practices and evidence patterns. Academic outcome differences should be interpreted in context and not treated as a direct school-to-school equivalency.
        </div>
      </Section>

      {/* 2. Score Gap Overview */}
      <Section title="Score Gap Overview" testid="benchmark-score-gap">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] min-w-[640px]">
            <thead className="text-left text-[11px] uppercase tracking-wide text-[#6b7280] border-b border-[#e5e7eb]">
              <tr><th className="py-2 pr-3">School</th><th className="py-2 px-3">Score</th><th className="py-2 px-3">Gap</th><th className="py-2 px-3">Risk</th><th className="py-2 px-3">Completion</th><th className="py-2 px-3">Evidence</th></tr>
            </thead>
            <tbody className="divide-y divide-[#f3f4f6]">
              {a.score_gap.map((s) => (
                <tr key={s.tenant_id} data-testid={`score-gap-row-${s.tenant_id}`} className={s.is_benchmark ? "bg-[#ecfdf5]/40" : ""}>
                  <td className="py-2.5 pr-3 font-semibold text-[#111827]">{s.school_name}{s.is_benchmark && <span className="ml-2 text-[10px] font-bold text-[#065f46]">BENCHMARK</span>}</td>
                  <td className="py-2.5 px-3 font-bold">{fmtScore(s.overall_score)}</td>
                  <td className="py-2.5 px-3 font-bold" style={{ color: s.is_benchmark ? "#065f46" : gapColor(s.gap_band) }}>{s.is_benchmark ? "0" : s.gap_to_benchmark}</td>
                  <td className="py-2.5 px-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${riskBadgeClass(s.risk_level)}`}>{s.risk_level || "—"}</span></td>
                  <td className="py-2.5 px-3 text-[#6b7280]">{s.completion_pct}%</td>
                  <td className="py-2.5 px-3 text-[#6b7280]">{s.evidence_completion_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* 3. Pillar Gap Comparison */}
      <Section title="Pillar Gap Comparison" sub="Gap = benchmark minus school. 'Portfolio Avg' = mean across all schools in this portfolio." testid="benchmark-pillar-gap">
        <BenchmarkDumbbell a={a} nonBench={nonBench} mode={pgMode} setMode={setPgMode} />
        <div className={`overflow-x-auto ${pgMode === "table" ? "" : "hidden"}`}>
          <table className="w-full text-[13px] min-w-[760px]">
            <thead className="text-left text-[11px] uppercase tracking-wide text-[#6b7280] border-b border-[#e5e7eb]">
              <tr>
                <th className="py-2 pr-3">Pillar</th><th className="py-2 px-3">Benchmark</th>
                <th className="py-2 px-3">Portfolio Avg</th>
                {nonBench.map((s) => <th key={s.tenant_id} className="py-2 px-3">{s.school_name.split(" ")[0]}…</th>)}
                <th className="py-2 px-3">Controllability</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#f3f4f6]">
              {a.pillar_gap.map((p) => (
                <tr key={p.pillar_id} data-testid={`pillar-gap-row-${p.pillar_id}`} className={p.in_scope ? "" : "opacity-50"}>
                  <td className="py-2.5 pr-3 font-semibold text-[#111827]">{p.pillar_name}{p.context_flag && <div className="text-[10px] font-normal text-[#a16207]">{p.context_flag}</div>}</td>
                  <td className="py-2.5 px-3 font-bold">{fmtScore(p.benchmark_score)}</td>
                  <td className="py-2.5 px-3 text-[#6b7280]" data-testid={`pillar-portfolio-avg-${p.pillar_id}`}>{fmtScore(p.portfolio_avg)}</td>
                  {nonBench.map((s) => {
                    const cell = p.schools.find((x) => x.tenant_id === s.tenant_id) || {};
                    return (
                      <td key={s.tenant_id} className="py-2.5 px-3">
                        {fmtScore(cell.score)} {cell.gap != null && cell.gap > 0 && <span className="text-[11px] font-bold text-[#00698a]">(-{cell.gap})</span>}
                        {cell.vs_portfolio != null && (
                          <div className="text-[10px] font-semibold" style={{ color: cell.vs_portfolio >= 0 ? "#065f46" : "#b91c1c" }}>
                            {cell.vs_portfolio >= 0 ? "+" : ""}{cell.vs_portfolio} vs portfolio
                          </div>
                        )}
                      </td>
                    );
                  })}
                  <td className="py-2.5 px-3"><span className="text-[11px] text-[#6b7280]">{p.controllability}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* 4. Indicator Opportunities */}
      <Section title="Indicator-Level Improvement Opportunities" sub="Management/process-controllable indicators where the benchmark leads." testid="benchmark-indicator-opps">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[11px] text-[#6b7280]">Sort:</span>
          <Select value={oppSort} onValueChange={setOppSort}>
            <SelectTrigger className="h-8 w-44 text-[12px]" data-testid="benchmark-opp-sort"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="gap">Largest gap</SelectItem><SelectItem value="priority">Highest priority</SelectItem><SelectItem value="evidence">Most missing evidence</SelectItem></SelectContent>
          </Select>
        </div>
        {!opps.length ? <div className="text-sm text-[#6b7280] py-4 text-center">No material indicator gaps in this scope.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] min-w-[760px]">
              <thead className="text-left text-[11px] uppercase tracking-wide text-[#6b7280] border-b border-[#e5e7eb]">
                <tr><th className="py-2 pr-3">School</th><th className="py-2 px-3">Pillar</th><th className="py-2 px-3">Indicator</th><th className="py-2 px-3">Bench</th><th className="py-2 px-3">School</th><th className="py-2 px-3">Gap</th><th className="py-2 px-3">Evidence</th><th className="py-2 px-3">Priority</th></tr>
              </thead>
              <tbody className="divide-y divide-[#f3f4f6]">
                {opps.slice(0, 50).map((o, i) => (
                  <tr key={i} data-testid={`indicator-opp-${i}`}>
                    <td className="py-2 pr-3 text-[#111827]">{o.school_name}</td>
                    <td className="py-2 px-3 text-[#6b7280]">{o.pillar_name}</td>
                    <td className="py-2 px-3 font-medium text-[#111827]">{o.indicator_name}</td>
                    <td className="py-2 px-3">{o.benchmark_score}</td>
                    <td className="py-2 px-3">{o.school_score}</td>
                    <td className="py-2 px-3 font-bold text-[#00698a]">{o.gap}</td>
                    <td className="py-2 px-3"><span className={`text-[10px] px-1.5 py-0.5 rounded ${o.evidence_status === "Missing" ? "text-[#b91c1c] bg-[#fef2f2]" : "text-[#065f46] bg-[#ecfdf5]"}`}>{o.evidence_status}</span></td>
                    <td className="py-2 px-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${PRIO[o.priority]}`}>{o.priority}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* 5. Evidence Practice Gaps */}
      <Section title="Evidence Practice Gaps" testid="benchmark-evidence-gaps">
        <div className="grid gap-3 sm:grid-cols-2">
          {a.evidence_gaps.map((e) => (
            <div key={e.tenant_id} className="rounded-lg border border-[#e5e7eb] p-3">
              <div className="font-bold text-[#111827] text-[14px]">{e.school_name}</div>
              <div className="text-[12px] text-[#6b7280] mt-1">{e.summary_text}</div>
              <div className="text-[11px] text-[#9ca3af] mt-1">Evidence completion: {e.evidence_completion_pct}%</div>
            </div>
          ))}
        </div>
      </Section>

      {/* 6. Recommended Actions */}
      <Section title="Recommended Actions" sub="Practical, management-controllable actions for lower-scoring schools." testid="benchmark-recommendations">
        <div className="space-y-5">
          {Object.entries(a.recommendations).map(([tid, recos]) => {
            const nm = a.score_gap.find((x) => x.tenant_id === tid)?.school_name || tid;
            if (!recos.length) return null;
            return (
              <div key={tid} data-testid={`reco-school-${tid}`}>
                <div className="text-[13px] font-bold text-[#111827] mb-2">{nm}</div>
                <div className="grid gap-3 lg:grid-cols-2">
                  {recos.map((r, i) => (
                    <div key={i} data-testid={`reco-card-${tid}-${i}`} className="rounded-lg border border-[#e5e7eb] p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-bold text-[#111827] text-[14px]">{r.action_title}</div>
                        <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded border ${PRIO[r.priority]}`}>{r.priority}</span>
                      </div>
                      <div className="text-[11px] text-[#6b7280] mt-1">{r.pillar_name} · Gap {r.current_gap}{r.context_flag && <span className="text-[#a16207]"> · {r.context_flag}</span>}</div>
                      <p className="text-[12px] text-[#374151] mt-2">{r.why_it_matters}</p>
                      <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] text-[#6b7280]">
                        <div><span className="text-[#9ca3af]">Owner:</span> {r.suggested_owner_role}</div>
                        <div><span className="text-[#9ca3af]">Timeline:</span> {r.suggested_timeline}</div>
                        <div><span className="text-[#9ca3af]">Impact:</span> {r.expected_score_impact}</div>
                        <div><span className="text-[#9ca3af]">Difficulty:</span> {r.difficulty_level}</div>
                      </div>
                      <div className="mt-2 text-[11px] text-[#6b7280]"><span className="text-[#9ca3af]">Evidence to collect:</span> {r.evidence_to_collect}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* 7. Priority Improvement Plan */}
      <Section title="Priority Improvement Plan" sub="Top 5 management actions per school, grouped by timeline." testid="benchmark-priority-plan">
        <div className="space-y-4">
          {Object.entries(a.priority_plan).map(([tid, plan]) => {
            const nm = a.score_gap.find((x) => x.tenant_id === tid)?.school_name || tid;
            const groups = [["Immediate (0–30 days)", plan.immediate], ["Short-Term (31–60 days)", plan.short_term], ["Board-Cycle (61–90 days)", plan.board_cycle]];
            return (
              <div key={tid} data-testid={`plan-school-${tid}`}>
                <div className="text-[13px] font-bold text-[#111827] mb-2">{nm}</div>
                <div className="grid gap-3 md:grid-cols-3">
                  {groups.map(([label, items]) => (
                    <div key={label} className="rounded-lg border border-[#e5e7eb] p-3">
                      <div className="text-[11px] uppercase tracking-wider text-[#6b7280] font-semibold mb-2">{label}</div>
                      {!items.length ? <div className="text-[11px] text-[#9ca3af]">—</div> : items.map((r, i) => (
                        <div key={i} className="text-[12px] text-[#374151] mb-2 flex gap-1.5">
                          <span className={`mt-0.5 h-1.5 w-1.5 rounded-full shrink-0`} style={{ background: r.priority === "High" ? "#ef4444" : r.priority === "Medium" ? "#facc15" : "#10b981" }} />
                          <span>{r.action_title} <span className="text-[#9ca3af]">({r.suggested_owner_role})</span></span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* AI panel */}
      {!config.ai_enabled ? (
        <div data-testid="benchmark-ai-locked" className="rounded-xl border border-[#e5e7eb] bg-[#fafafa] p-5 flex items-start gap-3">
          <Lock className="h-5 w-5 text-[#9ca3af] mt-0.5" />
          <div>
            <div className="font-bold text-[#111827] text-[14px] flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#b91c1c]" /> AI Benchmark Recommendations</div>
            <p className="text-[12px] text-[#6b7280] mt-1">AI Benchmark Recommendations are available as an upgrade. Enable this feature to generate management-focused action plans based on cross-school Diagnostic gaps.</p>
          </div>
        </div>
      ) : (
        <div data-testid="benchmark-ai-panel" className="rounded-xl border border-[#a7f3d0] bg-[#f0fdf4] p-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="font-bold text-[#111827] text-[14px] flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#065f46]" /> AI Benchmark Recommendations</div>
            <Button size="sm" onClick={generateAI} disabled={aiLoading} data-testid="benchmark-generate-ai" className="bg-[#065f46] hover:bg-[#064e3b] text-white">
              {aiLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />} {aiData ? "Regenerate" : "Generate AI Recommendations"}
            </Button>
          </div>
          {aiData && (
            <div className="mt-4 space-y-4" data-testid="benchmark-ai-results">
              {aiData.overall_note && <p className="text-[12px] text-[#065f46] bg-white/60 rounded-lg p-3">{aiData.overall_note}</p>}
              {(aiData.schools || []).map((s, si) => (
                <div key={si} data-testid={`ai-school-${si}`} className="rounded-lg border border-[#a7f3d0] bg-white p-4">
                  <div className="font-bold text-[#111827] text-[14px]">{s.school}</div>
                  {s.summary && <p className="text-[12px] text-[#374151] mt-1">{s.summary}</p>}
                  <div className="mt-3 grid gap-2 lg:grid-cols-2">
                    {(s.actions || []).map((ac, ai2) => (
                      <div key={ai2} className="rounded-md border border-[#e5e7eb] p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-semibold text-[#111827] text-[13px]">{ac.title}</div>
                          <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded border ${PRIO[ac.priority] || PRIO.Low}`}>{ac.priority}</span>
                        </div>
                        <div className="text-[11px] text-[#6b7280] mt-0.5">{ac.pillar}</div>
                        {ac.why && <p className="text-[12px] text-[#374151] mt-1">{ac.why}</p>}
                        <div className="mt-2 grid grid-cols-2 gap-1.5 text-[11px] text-[#6b7280]">
                          <div><span className="text-[#9ca3af]">Owner:</span> {ac.owner}</div>
                          <div><span className="text-[#9ca3af]">Timeline:</span> {ac.timeline}</div>
                          <div><span className="text-[#9ca3af]">Impact:</span> {ac.impact}</div>
                        </div>
                        {ac.evidence && <div className="mt-1 text-[11px] text-[#6b7280]"><span className="text-[#9ca3af]">Evidence:</span> {ac.evidence}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <p className="text-[10px] text-[#9ca3af]">AI-generated draft. {a.disclaimer}</p>
            </div>
          )}
        </div>
      )}

      <p className="text-[11px] text-[#9ca3af]">{a.disclaimer}</p>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 pt-2">
        <Button onClick={onSave} disabled={running} data-testid="benchmark-save-btn"><Save className="h-4 w-4 mr-1" /> Save Analysis</Button>
        <Button variant="outline" onClick={onExport} data-testid="benchmark-export-csv"><Download className="h-4 w-4 mr-1" /> Export CSV</Button>
        <Button variant="outline" onClick={exportPdf} disabled={pdfLoading} data-testid="benchmark-export-pdf">
          {pdfLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileText className="h-4 w-4 mr-1" />} Export PDF
        </Button>
      </div>
    </div>
  );
}
